/**
 * True eight directions (lane C0-8DIR, user 2026-10-10; switch: sprites.ts `DIR8`). The three headings that face
 * left (SW, W, NW) used to be the right-facing frames mirrored, which moved the weapon to the left hand, turned the
 * wrap collar to 左衽, put the scabbard on the wrong hip and the light at the upper left.
 *
 * A left view (`front3L`, `sideL`, `back3L`) is now its own frame, built from the same parts as the right-facing
 * view it comes from, with nothing redrawn:
 * 1. skeleton.ts (`left`): the pose as in the right-facing view, but with the body's sides exchanged — in depth
 *    (the weapon side is the far side now) and, three-quarter on, across the screen. Seen in a mirror that is the
 *    true left-facing body. Every part that reads its sides from the skeleton follows (collar, scabbard hip,
 *    gripping hand, draw order), and every per-view table (faces, hair, headwear) is the base view's.
 * 2. `leftFinish`: the composed frame is mirrored about the feet, lit (light from the upper right, as every frame
 *    is), and mirrored back. The renderer still flips these frames, as it flipped the old ones, so trails, blade
 *    lines and back-worn gear that follow the facing work as before, and the blade lines need no change.
 */
import { atRes, canvas, ctx2d, resOf } from '../pixel';
import { FRAME } from './figure';
import { litFinish } from './finish';

/** A full frame mirrored about the feet column (`FRAME.ax`), as the renderer mirrors a figure. */
function mirrored(c: HTMLCanvasElement): HTMLCanvasElement {
  const res = resOf(c);
  const out = canvas(c.width, c.height);
  const g = ctx2d(out);
  g.translate(FRAME.ax * res * 2, 0);
  g.scale(-1, 1);
  g.drawImage(c, 0, 0);
  return atRes(out, res);
}

/** The lit finish on a left view's composed (full, uncropped) frame, stored the way the renderer will flip it. */
export function leftFinish(c: HTMLCanvasElement): HTMLCanvasElement {
  return mirrored(litFinish(mirrored(c)));
}
